import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SUPPORTED_LANGUAGES, ensureLanguageLoaded } from '../i18n'
import { FLAGS } from '../utils/flags'
import '../css/LanguageSwitcher.css'

const LanguageSwitcher = ({ onSelect }) => {
	const { i18n, t } = useTranslation('common')
	const [isOpen, setIsOpen] = useState(false)
	const [isSwitching, setIsSwitching] = useState(false)
	const wrapperRef = useRef(null)

	const currentLanguage =
		SUPPORTED_LANGUAGES.find(l => l.code === i18n.language) || SUPPORTED_LANGUAGES[0]

	useEffect(() => {
		const preload = () => {
			SUPPORTED_LANGUAGES.forEach(lang => {
				const img = new Image()
				img.src = FLAGS[lang.flagCode]
			})
		}

		if (document.readyState === 'complete') {
			preload()
			return
		}
		window.addEventListener('load', preload, { once: true })
		return () => window.removeEventListener('load', preload)
	}, [])

	useEffect(() => {
		if (!isOpen) return

		const handleOutside = e => {
			if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
				setIsOpen(false)
			}
		}
		const handleKeyDown = e => {
			if (e.key === 'Escape') setIsOpen(false)
		}

		document.addEventListener('mousedown', handleOutside)
		document.addEventListener('keydown', handleKeyDown)
		return () => {
			document.removeEventListener('mousedown', handleOutside)
			document.removeEventListener('keydown', handleKeyDown)
		}
	}, [isOpen])

	const handleSelect = async code => {
		setIsOpen(false)
		onSelect?.()
		if (code === currentLanguage.code) return
		setIsSwitching(true)
		try {
			await ensureLanguageLoaded(code)
			await i18n.changeLanguage(code)
		} catch (error) {
			// Typically a lazy chunk that could not be fetched (offline): stay on the
			// current language and let the user try again.
			console.error('Language switch failed', error)
		} finally {
			setIsSwitching(false)
		}
	}

	return (
		<div className='language-switcher' ref={wrapperRef}>
			<button
				type='button'
				className='language-switcher-trigger'
				aria-label={t('languageSwitcher.label')}
				aria-expanded={isOpen}
				aria-busy={isSwitching}
				disabled={isSwitching}
				onClick={() => setIsOpen(prev => !prev)}
			>
				<img src={FLAGS[currentLanguage.flagCode]} alt='' className='language-switcher-flag' />
				<span className='language-switcher-current'>{currentLanguage.code.toUpperCase()}</span>
			</button>

			{isOpen && (
				<ul className='language-switcher-menu' role='listbox'>
					{SUPPORTED_LANGUAGES.map(lang => (
						<li key={lang.code}>
							<button
								type='button'
								role='option'
								aria-selected={lang.code === currentLanguage.code}
								className={`language-switcher-option ${lang.code === currentLanguage.code ? 'active' : ''}`}
								onClick={() => handleSelect(lang.code)}
							>
								<img src={FLAGS[lang.flagCode]} alt='' className='language-switcher-flag' />
								{lang.label}
							</button>
						</li>
					))}
				</ul>
			)}
		</div>
	)
}

export default LanguageSwitcher
